import React from 'react';
import PropTypes from 'prop-types';
import { Button, Popover } from 'antd';
import {
  FIX_MISSING_FILES, PHASE_LABELS, PHASE_SHORT_LABELS, progressLabel, progressPercent, progressSummary,
} from './startProgress';
import './sandbox.css';

const shortName = (serviceName) => String(serviceName).replace(/^services\//, '');

/** Asks the project's view to open Apps on `serviceName` with "Provide the missing files" open (UnifiedTopBar listens). */
export const PROVIDE_MISSING_EVENT = 'flow:provide-missing-files';
export function requestProvideMissing(serviceName) {
  if (typeof window === 'undefined') { return; }
  window.dispatchEvent(new CustomEvent(PROVIDE_MISSING_EVENT, { detail: { serviceName } }));
}

/** One line per service: its name, where it is, and why it failed (with Logs). */
export function StartProgressList({ progress, onOpenLogs = undefined }) {
  return (
    <ul className="sbx-start-list" aria-label="Services">
      {progress.services.map((service) => (
        <li className={`sbx-start-item sbx-start-${service.phase}`} key={service.serviceName}>
          <span className="sbx-start-name">{shortName(service.serviceName)}</span>
          <span className="sbx-start-phase">
            {PHASE_LABELS[service.phase] || service.phase}
            {service.percent !== null && service.percent !== undefined && !['running', 'failed', 'limit', 'stopped'].includes(service.phase) ? ` · ${service.percent}%` : ''}
          </span>
          {service.reason && (
            <span className="sbx-start-reason">
              {service.reason}
              {service.phase === 'failed' && service.fix === FIX_MISSING_FILES && (
                <>
                  {' '}
                  <button className="sbx-start-logs" onClick={() => requestProvideMissing(service.serviceName)} type="button">Provide the missing files</button>
                </>
              )}
              {service.phase === 'failed' && onOpenLogs && service.fix !== FIX_MISSING_FILES && (
                <>
                  {' '}
                  <button className="sbx-start-logs" onClick={() => onOpenLogs(service.serviceName)} type="button">Logs</button>
                </>
              )}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

StartProgressList.propTypes = {
  progress: PropTypes.shape({ services: PropTypes.arrayOf(PropTypes.object).isRequired }).isRequired,
  onOpenLogs: PropTypes.func,
};

/** What a start says aloud (a polite live region): where it is, then "All 3 services are running" or what failed. */
export function StartAnnouncer({ progress = null }) {
  const summary = progressSummary(progress);
  const single = summary.total === 1 ? progress?.services[0] : null;
  const live = (() => {
    if (!progress || !summary.total) { return ''; }
    if (summary.active) { return single ? `${shortName(single.serviceName)}: ${PHASE_LABELS[single.phase]}` : progressLabel(progress); }
    if (summary.running === summary.total) { return single ? `${shortName(single.serviceName)} is running` : `All ${summary.total} services are running`; }
    return progressLabel(progress);
  })();
  return <span aria-live="polite" className="sbx-sr-only" role="status">{live}</span>;
}

StartAnnouncer.propTypes = {
  progress: PropTypes.shape({ services: PropTypes.arrayOf(PropTypes.object).isRequired }),
};

/**
 * The Start / Start all button with its progress. Idle (`progress` null, or everything started): `label`, which calls
 * `onStart`. While services start: "Starting 1/3…" (one service: its step, "Installing dependencies…") with a bar
 * under the words; hover or focus lists each service and its step. When some did not start: "2/3 running · 1 failed"
 * with the list and Retry (`onRetry`; `retryType` its antd type: primary, or default in a list row beside light buttons). The words are also said once more in a polite live region; the button keeps its
 * width (no jump), says `aria-busy` while it works and stays focusable so the list can be read.
 */
export default function StartButton({
  label, progress = null, onStart, onRetry = undefined, onOpenLogs = undefined, onDismiss = undefined, disabled = false,
  size = 'small', type = 'primary', retryType = 'primary', ariaLabel = undefined, className = '', announce = true,
}) {
  const summary = progressSummary(progress);
  const words = progress ? progressLabel(progress) : null;
  const active = Boolean(progress) && summary.active;
  // Over with something that did not start (a failure, the plan's limit); services the person stopped are not one.
  const ended = Boolean(progress) && summary.done && summary.total > 0 && summary.running < summary.total
    && (summary.failed > 0 || summary.limited > 0);
  const single = summary.total === 1 ? progress?.services[0] : null;
  // `announce` false: the caller keeps the live region where it stays mounted (a region only speaks of changes).
  const status = announce ? <StartAnnouncer progress={progress} /> : null;

  if (!active && !ended) {
    return (
      <span className={`sbx-start ${className}`}>
        <Button aria-label={ariaLabel} className="sbx-start-btn" disabled={disabled} onClick={onStart} size={size} type={type}>
          {label}
        </Button>
        {status}
      </span>
    );
  }

  const list = <StartProgressList onOpenLogs={onOpenLogs} progress={progress} />;
  if (active) {
    const percent = progressPercent(progress);
    const text = single
      ? `${PHASE_SHORT_LABELS[single.phase]}${single.percent !== null && single.percent !== undefined ? ` ${single.percent}%` : ''}…`
      : words;
    return (
      <span className={`sbx-start ${className}`}>
        <Popover content={list} placement="bottom" title={single ? `Starting ${shortName(single.serviceName)}` : 'Starting services'} trigger={['hover', 'focus', 'click']}>
          <Button
            aria-busy="true"
            className="sbx-start-btn sbx-start-busy"
            size={size}
            type={type}
          >
            <span className="sbx-start-text">{text}</span>
            <span aria-hidden="true" className="sbx-start-track"><span className="sbx-start-fill" style={{ width: `${Math.max(4, percent)}%` }} /></span>
          </Button>
        </Popover>
        {status}
      </span>
    );
  }

  return (
    <span className={`sbx-start ${className}`}>
      <Popover content={list} placement="bottom" title="Not everything started" trigger={['hover', 'focus', 'click']}>
        <Button className="sbx-start-btn sbx-start-ended" danger size={size}>
          {words}
        </Button>
      </Popover>
      {onRetry && <Button className="sbx-start-retry" onClick={onRetry} size={size} type={retryType}>Retry</Button>}
      {onDismiss && <Button aria-label="Dismiss" onClick={onDismiss} size={size} type="text">✕</Button>}
      {status}
    </span>
  );
}

StartButton.propTypes = {
  label: PropTypes.node.isRequired,
  progress: PropTypes.shape({ services: PropTypes.arrayOf(PropTypes.object).isRequired, done: PropTypes.bool }),
  onStart: PropTypes.func.isRequired,
  onRetry: PropTypes.func,
  onOpenLogs: PropTypes.func,
  onDismiss: PropTypes.func,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'middle', 'large']),
  type: PropTypes.string,
  retryType: PropTypes.string,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  announce: PropTypes.bool,
};
